if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          if (this.hasAttribute('data-rc-quick-view-modal')) {
            target.postProcessHtmlCallbacks.push(() => {
              queueMicrotask(() => this.observeSupplementModal());
            });
          }
          if (this.rcQuickView) {
            target.postProcessHtmlCallbacks.push(() => {
              queueMicrotask(() => { void this.rcQuickView?.updateSelection(); });
            });
          }
        });
      }

      hide(preventFocus = false) {
        if (this.rcQuickView) {
          if (this.rcQuickViewClosing) return this.rcQuickViewClosing;
          const rechargeView = this.rcQuickView;
          rechargeView.leave();
          this.rcQuickViewClosing = Promise.resolve(super.hide()).then(() => {
            this.removeSupplementModal();
            this.modalContent.innerHTML = '';
            delete this.rcQuickView;
            this.removeAttribute('data-rc-animated-modal');
            this.modalContent.removeAttribute('data-rc-product-quick-view-content');
            delete this.modalContent.rcQuickView;
            rechargeView.restoreCatalog();
          }).finally(() => { this.rcQuickViewClosing = null; });
          return this.rcQuickViewClosing;
        }
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
        this.removeSupplementModal();
      }

      show(opener, rechargeView = null) {
        if (this.rcQuickViewClosing) return this.rcQuickViewClosing.then(() => this.show(opener, rechargeView));
        if (
          !rechargeView &&
          !opener.hasAttribute('data-storefront-quick-view') &&
          opener.closest('.account-recommendations')
        ) {
          const account = opener.closest('.rc-account[data-wrapper]')?.querySelector('rc-account');
          if (account?.openProductQuickView) return account.openProductQuickView(opener, this);
        }
        if (rechargeView) {
          this.rcQuickView = rechargeView;
          return rechargeView.openInModal(opener, this, () => super.show(opener));
        }
        if (this.hasAttribute('data-rc-quick-view-modal')) {
          this.modalContent.className = 'modal__product';
          if (this.rcHandleClass) this.classList.remove(this.rcHandleClass);
          this.rcHandleClass = opener.dataset.rcProductHandle
            ? 'modal--' + opener.dataset.rcProductHandle
            : null;
          if (this.rcHandleClass) this.classList.add(this.rcHandleClass);
        }
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');
        return this.loadProduct(opener)
          .then(() => super.show(opener))
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      async loadProduct(opener, beforeRender) {
        const response = await fetch(opener.getAttribute('data-product-url'));
        if (!response.ok) throw new Error('Product page unavailable');
        const responseHTML = new DOMParser().parseFromString(await response.text(), 'text/html');
        const productElement = responseHTML.querySelector('product-info');
        if (!productElement) throw new Error('PDP content missing');
        this.preprocessHTML(productElement);
        if (beforeRender) await beforeRender(productElement);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
        this.observeSupplementModal();
        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        if (this.hasAttribute('data-rc-quick-view-modal')) {
          this.removeSupplementModal();
          const supplementModal = productElement.querySelector(
            'modal-dialog#PopupModal-supplement-data',
          );
          if (supplementModal) {
            const modalId = `PopupModal-supplement-data-${this.id}`;
            supplementModal.id = modalId;
            supplementModal.querySelector('[id^="ModalClose-"]')?.setAttribute(
              'id',
              `ModalClose-${modalId}`,
            );
            productElement
              .querySelectorAll('modal-opener[data-modal="#PopupModal-supplement-data"]')
              .forEach((opener) => opener.setAttribute('data-modal', `#${modalId}`));
          }
        }
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog)
          modalDialog.forEach((modal) => {
            if (
              this.hasAttribute('data-rc-quick-view-modal') &&
              modal.id === `PopupModal-supplement-data-${this.id}`
            )
              return;
            modal.remove();
          });
      }

      removeSupplementModal() {
        this.supplementModalObserver?.disconnect();
        this.supplementModalObserver = null;
        document.getElementById(`PopupModal-supplement-data-${this.id}`)?.remove();
      }

      observeSupplementModal() {
        if (!this.hasAttribute('data-rc-quick-view-modal')) return;
        this.supplementModalObserver?.disconnect();
        const supplementModal = document.getElementById(
          `PopupModal-supplement-data-${this.id}`,
        );
        if (!supplementModal) return;
        this.supplementModalObserver = new MutationObserver(() => {
          if (supplementModal.hasAttribute('open') || !this.hasAttribute('open'))
            return;
          document.body.classList.add('overflow-hidden');
          trapFocus(this, this.querySelector('[role="dialog"]'));
          this.querySelector(
            `modal-opener[data-modal="#${supplementModal.id}"] button`,
          )?.focus();
        });
        this.supplementModalObserver.observe(supplementModal, {
          attributes: true,
          attributeFilter: ['open'],
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
